/**
 * usePastLivesRun — polls GET /api/v1/past-lives/runs/:runId and
 *                  GET /api/v1/past-lives/runs/:runId/cards.
 *
 * Behavior mirrors useAvatarPack: starts polling when a runId is provided,
 * stops once the run reaches a terminal status (READY / FAILED / REFUNDED).
 * Returns the full card list every tick so the UI can progressively reveal
 * cards as they become available (worker delivers them via an async generator,
 * so cardCount climbs over the lifetime of the run).
 */

import { useEffect, useRef, useState } from 'react';
import { apiFetch } from '../lib/api-client';
import { useAuth } from '../store/auth';

export type PastLivesRunStatus =
  | 'PENDING'
  | 'GENERATING'
  | 'READY'
  | 'FAILED'
  | 'REFUNDED';

export interface PastLivesRunSummary {
  id: string;
  tier: string;
  status: PastLivesRunStatus;
  paid: boolean;
  starsAmount: number | null;
  totalCostUsd: number | null;
  cardCount: number;
  error: string | null;
  startedAt: string | null;
  completedAt: string | null;
  createdAt: string;
}

export interface PastLivesCard {
  id: string;
  layer: string;
  layerDisplayRu: string;
  isHero: boolean;
  deliveryIndex: number;
  profession: string | null;
  region: string;
  era: string;
  eraYearEstimate: number;
  gender: string;
  socialClass: string;
  attireHint: string | null;
  narrative: string;
  tokens: string[];
  portraitUrl: string;
  source: string;
  model: string;
  costUsd: number;
  createdAt: string;
}

export interface PastLivesSnapshot {
  run: PastLivesRunSummary;
  cards: PastLivesCard[];
}

interface UsePastLivesRunResult {
  snapshot: PastLivesSnapshot | null;
  error: Error | null;
}

const POLL_INTERVAL_MS = 3000;
const POLL_TIMEOUT_MS = 15 * 60 * 1000;

export function usePastLivesRun(runId: string | null | undefined): UsePastLivesRunResult {
  const jwt = useAuth((s) => s.jwt);
  const [snapshot, setSnapshot] = useState<PastLivesSnapshot | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const cancelledRef = useRef(false);

  useEffect(() => {
    cancelledRef.current = false;
    setSnapshot(null);
    setError(null);
    if (!runId || !jwt) return;

    const startedAt = Date.now();
    let timer: number | undefined;

    const tick = async (): Promise<void> => {
      if (cancelledRef.current) return;
      try {
        const [runRes, cardsRes] = await Promise.all([
          apiFetch(`/api/v1/past-lives/runs/${runId}`, jwt, { method: 'GET' }),
          apiFetch(`/api/v1/past-lives/runs/${runId}/cards`, jwt, { method: 'GET' }),
        ]);
        if (!runRes.ok) throw new Error(`run_fetch_${runRes.status}`);
        if (!cardsRes.ok) throw new Error(`cards_fetch_${cardsRes.status}`);
        const run = (await runRes.json()) as PastLivesRunSummary;
        const cardsData = (await cardsRes.json()) as { runStatus: string; cards: PastLivesCard[] };
        if (cancelledRef.current) return;
        setSnapshot({ run, cards: cardsData.cards });
        if (run.status === 'READY' || run.status === 'FAILED' || run.status === 'REFUNDED') return;
        if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
          throw new Error('run_poll_timeout');
        }
        timer = window.setTimeout(() => void tick(), POLL_INTERVAL_MS);
      } catch (err) {
        if (cancelledRef.current) return;
        setError(err as Error);
      }
    };

    void tick();
    return () => {
      cancelledRef.current = true;
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [runId, jwt]);

  return { snapshot, error };
}
